import { useCallback, useEffect, useState } from 'react';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';
import type { BetaWelcomeDmSettings } from '../types/beta-welcome';
import {
  BetaAdminActions,
  BetaAdminCard,
  BetaAdminField,
  betaAdminInputProps,
  betaAdminTextareaProps,
} from './BetaAdminUi';
import { Switch } from './Switch';

function adminFetch(url: string, pseudo: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'X-Admin-Pseudo': pseudo,
    },
  });
}

interface BetaWelcomeDmAdminProps {
  session: Session;
  busy: boolean;
  onBusyChange: (busy: boolean) => void;
  onFeedback: (message: string) => void;
  onSettingsChange?: (settings: BetaWelcomeDmSettings) => void;
}

export default function BetaWelcomeDmAdmin({
  session,
  busy,
  onBusyChange,
  onFeedback,
  onSettingsChange,
}: BetaWelcomeDmAdminProps) {
  const [settings, setSettings] = useState<BetaWelcomeDmSettings>({
    enabled: false,
    authorPseudo: 'panurge',
    messageTemplate: '',
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/welcome-dm?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
      );
      if (!res.ok) throw new Error('Chargement impossible');
      const data = await res.json() as { settings: BetaWelcomeDmSettings };
      setSettings(data.settings);
      onSettingsChange?.(data.settings);
    } catch (err) {
      onFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [onFeedback, onSettingsChange, session.pseudo]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    onBusyChange(true);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/welcome-dm?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(settings),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setSettings(data.settings);
      onSettingsChange?.(data.settings);
      onFeedback('Message privé de bienvenue enregistré');
    } catch (err) {
      onFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      onBusyChange(false);
    }
  };

  const disabled = busy || loading;

  return (
    <BetaAdminCard
      icon="💬"
      title="Message privé de bienvenue"
      description="Envoi optionnel lors de l'ajout d'un bêta-testeur, pour lui souhaiter la bienvenue via la messagerie."
      footer={(
        <BetaAdminActions align="end">
          <button
            type="button"
            className="beta-admin-btn beta-admin-btn--primary"
            disabled={disabled}
            onClick={() => void save()}
          >
            Enregistrer
          </button>
        </BetaAdminActions>
      )}
    >
      <label className="beta-admin-toggle">
        <span>
          Activer par défaut à l&apos;ajout d&apos;un bêta-testeur
        </span>
        <Switch
          checked={settings.enabled}
          onChange={(next) => setSettings(s => ({ ...s, enabled: next }))}
          disabled={disabled}
        />
      </label>

      <div className="beta-admin-form-grid beta-admin-dm-fields">
        <BetaAdminField label="Auteur du message" htmlFor="beta-welcome-dm-author">
          <input
            id="beta-welcome-dm-author"
            type="text"
            {...betaAdminInputProps(disabled)}
            value={settings.authorPseudo}
            onChange={e => setSettings(s => ({ ...s, authorPseudo: e.target.value }))}
          />
        </BetaAdminField>

        <BetaAdminField
          label="Texte du message"
          hint="Variables : {pseudo}, {pseudoDisplay}"
          htmlFor="beta-welcome-dm-template"
          full
        >
          <textarea
            id="beta-welcome-dm-template"
            {...betaAdminTextareaProps(disabled, 6)}
            value={settings.messageTemplate}
            onChange={e => setSettings(s => ({ ...s, messageTemplate: e.target.value }))}
          />
        </BetaAdminField>
      </div>
    </BetaAdminCard>
  );
}
